// ===== Firebase =====

import fire                                 from '../fire';

// ===== Services =====

import {
    authenticateWithEmailCredentials,
    completeAuthenticatedUserSetup,
    getEmailConversionErrorMessage,
    createAuthFlowError,
    inferNameFromEmail,
}                                           from '.';
import resolvePreferredUserName             from './resolvePreferredUserName';

// ===== Interfaces =====

import {
    IAuthenticatedUserSetupOldUserData,
    IAuthFlowError,
    IEmailConversionErrorMessages,
}                                           from '../interfaces';

// ===== Enums =====

import {
    AUTHENTICATION_TYPE,
    EMAIL_CONVERSION_CALLABLE_ERROR_CODE,
    EMAIL_CREDENTIALS_AUTH_MODE,
}                                           from '../enums';

// ===== General Constants =====

import {
    SUPPORT_EMAIL_ADDRESS,
}                                           from '../constants/generalConstants';

const { auth } = fire();

const DEFAULT_EMAIL_CONVERSION_ERROR_MESSAGES: IEmailConversionErrorMessages = {
    fallback: 'Unable to finish creating your account right now.',
    emailInUse: (
        'An account already exists with this email. '
        + 'Please sign in with the method linked to this account or use another email address.'
    ),
    precheckUnavailable: `Unable to verify this email right now. Please contact support: ${SUPPORT_EMAIL_ADDRESS}.`,
    invalidEmail: 'Please enter a valid email address.',
    notAnonymous: 'Your account is already authenticated. Only anonymous users can be converted with this flow.',
    userNotFound: `We could not find your guest checkout session. Please contact support: ${SUPPORT_EMAIL_ADDRESS}.`,
    unauthenticated: 'Your session expired. Please sign in again and try again.',
    signInFallback: 'There was a problem signing in after account creation.',
};

// ** IMPORTANT **: Keep in sync with functions/src/index.ts
const CHECKOUT_TEMP_PASSWORD_LENGTH = 10;
const CHECKOUT_TEMP_PASSWORD_STATIC_SEGMENT = 'Aa1!';
const CHECKOUT_TEMP_PASSWORD_PREFIX_LENGTH = CHECKOUT_TEMP_PASSWORD_LENGTH / 2;
const CHECKOUT_TEMP_PASSWORD_RANDOM_SEGMENT_LENGTH = (
    CHECKOUT_TEMP_PASSWORD_LENGTH - CHECKOUT_TEMP_PASSWORD_STATIC_SEGMENT.length
);
const ALPHANUMERIC_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const MAX_UNBIASED_BYTE = 248; // 62 * 4

const generateSecureAlphaNumeric = (length: number): string => {
    const characters: string[] = [];

    while (characters.length < length) {
        const cryptoApi = globalThis.crypto;
        if (!cryptoApi?.getRandomValues) {
            break;
        }
        const requiredCharacters = length - characters.length;
        const randomBytes = new Uint8Array(requiredCharacters * 2);
        cryptoApi.getRandomValues(randomBytes);

        for (let i = 0; i < randomBytes.length && characters.length < length; i += 1) {
            const randomByte = randomBytes[i];
            if (randomByte < MAX_UNBIASED_BYTE) {
                const characterIndex = randomByte % ALPHANUMERIC_CHARACTERS.length;
                characters.push(ALPHANUMERIC_CHARACTERS[characterIndex]);
            }
        }
    }

    while (characters.length < length) {
        const fallbackIndex = Math.floor(Math.random() * ALPHANUMERIC_CHARACTERS.length);
        characters.push(ALPHANUMERIC_CHARACTERS[fallbackIndex]);
    }

    return characters.join('');
};

// ** IMPORTANT **: Keep in sync with generateTemporaryCheckoutPassword in functions/src/index.ts.
const generateTemporaryPassword = (): string => {
    const randomSegment = generateSecureAlphaNumeric(CHECKOUT_TEMP_PASSWORD_RANDOM_SEGMENT_LENGTH);
    return (
        `${randomSegment.substring(0, CHECKOUT_TEMP_PASSWORD_PREFIX_LENGTH)}`
        + `${CHECKOUT_TEMP_PASSWORD_STATIC_SEGMENT}`
        + `${randomSegment.substring(CHECKOUT_TEMP_PASSWORD_PREFIX_LENGTH)}`
    );
};

const authenticateWithEmailConversion = async ({
    test,
    hasSound,
    email,
    oldUserData,
    errorMessages,
}: {
    test: boolean,
    hasSound: boolean,
    email: string,
    oldUserData: IAuthenticatedUserSetupOldUserData,
    errorMessages?: Partial<IEmailConversionErrorMessages>,
}): Promise<{
    email: string,
}> => {
    const messages: IEmailConversionErrorMessages = {
        ...DEFAULT_EMAIL_CONVERSION_ERROR_MESSAGES,
        ...errorMessages,
    };
    const normalizedEmail = email.trim().toLowerCase();
    try {
        if (!auth.currentUser?.isAnonymous) {
            throw createAuthFlowError<IAuthFlowError>(
                messages.notAnonymous,
                EMAIL_CONVERSION_CALLABLE_ERROR_CODE.failedPrecondition,
            );
        }
        if (
            !oldUserData?.user
            || oldUserData.user.authenticationType !== AUTHENTICATION_TYPE.anonymous
        ) {
            throw createAuthFlowError<IAuthFlowError>(
                messages.userNotFound,
                EMAIL_CONVERSION_CALLABLE_ERROR_CODE.notFound,
            );
        }
        if (oldUserData.user.id !== auth.currentUser.uid) {
            throw createAuthFlowError<IAuthFlowError>(
                messages.notAnonymous,
                EMAIL_CONVERSION_CALLABLE_ERROR_CODE.failedPrecondition,
            );
        }

        const inferredName = inferNameFromEmail(normalizedEmail);
        const resolvedName = resolvePreferredUserName({
            preferredFirstName: oldUserData.user?.firstName,
            preferredLastName: oldUserData.user?.lastName,
            fallbackFirstName: inferredName.firstName,
            fallbackLastName: inferredName.lastName,
        });
        const resolvedOldUserData: IAuthenticatedUserSetupOldUserData = {
            user: oldUserData.user
                ? {
                    ...oldUserData.user,
                    firstName: resolvedName.firstName,
                    lastName: resolvedName.lastName,
                }
                : undefined,
            email: oldUserData.email || normalizedEmail,
            oldUserCart: oldUserData.oldUserCart,
            userSessions: oldUserData.userSessions,
            userUpdateTimestamps: oldUserData.userUpdateTimestamps,
        };
        const temporaryPassword = generateTemporaryPassword();
        await authenticateWithEmailCredentials({
            test,
            mode: EMAIL_CREDENTIALS_AUTH_MODE.signUp,
            hasSound,
            email: normalizedEmail,
            password: temporaryPassword,
            oldUserData: resolvedOldUserData,
        });
        await completeAuthenticatedUserSetup({
            test,
            oldUserData: resolvedOldUserData,
            onboardingTemporaryPassword: temporaryPassword,
        });
        return {
            email: normalizedEmail,
        };
    } catch (error) {
        const errorCode = (error as { code?: string } | undefined)?.code;
        const errorMessage = await getEmailConversionErrorMessage({
            email: normalizedEmail,
            error: {
                code: errorCode,
                message: (error as { message?: string } | undefined)?.message,
            },
            messages,
        });
        throw createAuthFlowError<IAuthFlowError>(errorMessage, errorCode);
    }
};

export default authenticateWithEmailConversion;
